import { AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';

/** Well-formed CAS number: <2–7 digits>-<2 digits>-<1 check digit> (e.g. 7732-18-5). */
const CAS_PATTERN = /^(\d{2,7})-(\d{2})-(\d)$/;

/**
 * The CAS checksum: take every digit except the check digit, multiply each by
 * its position counting from right to left (1-based), sum, take mod 10 — the
 * result must equal the check digit.
 */
function casChecksumMatches(intPart: string, unitPart: string, checkDigit: number): boolean {
  const digits = intPart + unitPart;
  let sum = 0;
  for (let i = 0; i < digits.length; i++) {
    // rightmost digit has position 1
    sum += (digits.length - i) * Number(digits[i]);
  }
  return sum % 10 === checkDigit;
}

/**
 * True when `value` is a well-formed CAS Registry Number whose check digit is
 * valid. Passing only proves the number is well-formed — not that the substance
 * is registered in the CAS database. Shared by the form validator and the
 * PDF CAS extractor.
 */
export function isValidCasNumber(value: string): boolean {
  const match = CAS_PATTERN.exec((value ?? '').toString().trim());
  return match ? casChecksumMatches(match[1], match[2], Number(match[3])) : false;
}

/**
 * Maximum length of the comma-joined CAS list, mirroring the width of
 * `SDS_Document.CasNumber` in the database. A PDF scan of a long SDS (engine
 * oils in particular) can yield dozens of valid CAS numbers, and a joined list
 * longer than the column used to fail the save with a 500 ("String or binary
 * data would be truncated").
 */
export const CAS_LIST_MAX_LENGTH = 900;

/** Joins CAS numbers into the single string the API stores. */
export function joinCasNumbers(list: readonly (string | null | undefined)[] | null | undefined): string {
  return (list ?? [])
    .map(value => (value ?? '').toString().trim())
    .filter(value => value.length > 0)
    .join(', ');
}

/**
 * Adds `found` to `existing`, skipping duplicates and any number that would push
 * the joined list past {@link CAS_LIST_MAX_LENGTH}. Entries already in
 * `existing` are never dropped, so a manually over-filled field keeps its value
 * (and stays flagged by {@link casListValidator}) instead of losing input.
 */
export function mergeCasNumbersWithinLimit(
  existing: readonly string[],
  found: readonly string[]
): { merged: string[]; added: string[]; skipped: string[] } {
  const merged = [...existing];
  const added: string[] = [];
  const skipped: string[] = [];

  for (const value of found) {
    if (merged.includes(value)) {
      continue;
    }
    if (joinCasNumbers([...merged, value]).length > CAS_LIST_MAX_LENGTH) {
      skipped.push(value);
      continue;
    }
    merged.push(value);
    added.push(value);
  }

  return { merged, added, skipped };
}

/** Splits a comma/semicolon/newline-separated CAS string into trimmed, non-empty parts. */
export function splitCasNumbers(value: string | null | undefined): string[] {
  return (value ?? '')
    .toString()
    .split(/[,;\n]+/)
    .map(part => part.trim())
    .filter(part => part.length > 0);
}

/**
 * Reactive-forms validator for a list (string[]) of CAS numbers — the chips
 * control. Requiredness is handled separately by the form and its explicit
 * no-CAS checkbox. When supplied, every entry must be valid and the joined
 * list must fit {@link CAS_LIST_MAX_LENGTH}. Reuses the same error keys as
 * {@link casNumberValidator} so the existing control-messages render, plus
 * `casTooLong` for the length rule.
 */
export const casListValidator: ValidatorFn =
  (control: AbstractControl): ValidationErrors | null => {
    const raw = control.value;
    const list: string[] = Array.isArray(raw) ? raw : splitCasNumbers(raw);
    const cleaned = list.map(v => (v ?? '').toString().trim()).filter(v => v.length > 0);

    if (cleaned.length === 0) {
      return null;
    }
    if (!cleaned.every(isValidCasNumber)) {
      return { casFormat: true };
    }
    // The list is stored as one comma-joined column value, so the combined
    // length is bounded even though each entry on its own is valid.
    return joinCasNumbers(cleaned).length > CAS_LIST_MAX_LENGTH
      ? { casTooLong: true }
      : null;
  };

/**
 * Reactive-forms validator for a CAS Registry Number.
 *
 * Returns:
 *   - null                    when empty (a separate "required" rule handles that)
 *   - { casFormat: true }     when the pattern is wrong
 *   - { casChecksum: true }   when the check digit doesn't match
 *
 * Server-side validation remains authoritative.
 */
export const casNumberValidator: ValidatorFn =
  (control: AbstractControl): ValidationErrors | null => {
    const value = (control.value ?? '').toString().trim();
    if (value.length === 0) {
      return null;
    }

    const match = CAS_PATTERN.exec(value);
    if (!match) {
      return { casFormat: true };
    }

    return casChecksumMatches(match[1], match[2], Number(match[3]))
      ? null
      : { casChecksum: true };
  };
